<template>
  <div
    class="asset"
  >
    <img
      v-if="asset.asset_type === 'image' || showPlaceholder"
      :src="imageUrl"
      :alt="asset.title"
      class="asset__media"
      :class="{
        'asset__media--cover': asset.fillType === 'cover',
      }"
      @error="showPlaceholder = true"
    >
    <video
      v-else-if="asset.asset_type === 'video'"
      ref="video"
      class="asset__media"
      :class="{
        'asset__media--cover': asset.fillType === 'cover',
      }"
      :src="asset.media_url"
      :autoplay="videoPlaying"
      :muted="isMuted"
      :poster="asset.thumbnail_url"
      :loop="shouldLoop"
      @loadedmetadata="handleLoadedMetaData"
      @error="showPlaceholder = true"
    ></video>
  </div>
</template>

<script>
import { preloadImage } from '../../utils/helpers';
import EventBus from '../../utils/event-bus';

export default {
  name: 'Asset',
  props: {
    asset: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      showPlaceholder: false,
    };
  },
  async mounted() {
    if (!this.isVideo) {
      await preloadImage(this.asset.media_url);
      EventBus.$emit('resetTimer');
      EventBus.$emit('startTimer', this.displayTime || 10000);
    }

    this.$nextTick(() => {
      this.$emit('frameloaded');
    });
  },
  computed: {
    isPlaying() {
      return this.$store.state.production.playing;
    },

    isMuted() {
      return this.$store.state.production.muted;
    },

    videoPlaying() {
      return this.$store.state.production.videoPlaying;
    },

    videoEl() {
      return this.$refs.video;
    },

    isVideo() {
      return this.asset.asset_type === 'video';
    },

    displayTime() {
      return this.asset.displayTime ? this.asset.displayTime : this.asset.settings.displayTime;
    },

    shouldLoop() {
      return this.asset.loop ?? false;
    },

    imageUrl() {
      if (this.showPlaceholder) {
        return `${process.env.ASSETS_CDN_URL}/placeholders/image-not-available.png`;
      }

      return this.asset.media_url;
    },
  },
  watch: {
    videoPlaying(newValue) {
      if (this.isVideo) {
        if (newValue) {
          this.play();
        } else {
          this.pause();
        }
      }
    },

    muted(newValue) {
      if (this.isVideo) {
        if (newValue) {
          this.muteVideo();
        } else {
          this.unmuteVideo();
        }
      }
    },

    // playlist is playing
    isPlaying(newValue) {
      if (newValue) {
        if (this.isVideo) {
          EventBus.$emit('resetTimer');
          // TODO: update timer with video duration?
        } else {
          const { production } = this.$store.state;
          if (production) {
            // If there's a "production" module in the store, then we are in producer so emit the time
            EventBus.$emit('startTimer', this.displayTime);
          }
        }
      }
    },
  },
  methods: {
    play() {
      if (this.videoPlaying) {
        this.videoEl.play();
      }
    },

    pause() {
      this.videoEl.pause();
    },

    muteVideo() {
      try {
        this.videoEl.muted = true;
        this.videoEl.setAttribute('muted', 'true');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },

    unmuteVideo() {
      try {
        this.videoEl.muted = false;
        this.videoEl.setAttribute('muted', 'false');
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error(error);
      }
    },

    handleLoadedMetaData() {
      const duration = this.videoEl.duration * 1e3;
      this.$store.dispatch('setPlayDuration', duration);
      this.$store.dispatch('setVideoDuration', duration);
    },
  },
};
</script>

<style lang="scss" scoped>
.asset {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  overflow: hidden;

  &__media {
    width: 100%;
    height: 100%;
    object-fit: contain;

    &--cover {
      object-fit: cover;
    }
  }
}
</style>
